import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La ville floue est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'rainwindow',
  family: 'organic',
  danceability: 0.32,
  energyLevel: 0.22,
  bpmMin: 55,
  bpmMax: 110,
  movement: 0.20,
  tonality: 0.85,
  enabled: true
};

// Rainwindow : la pluie sur une vitre, la nuit. Derrière, les lumières
// floues d'une ville qui respirent avec la musique ; devant, des gouttes
// qui coulent et réfractent ces lumières. Lo-fi, jazz, R&B lent, chanson,
// ambient. Gamme 0,30-0,55 (R9) : preset lounge, aucun flash, aucune
// impulsion ; seule la structure se voit (une goutte qui part sur une
// phrase, cinq sur un drop), à des intervalles où l'on ne devine pas le
// tempo.
//
// LA VILLE (bokeh), procédurale : 120 disques doux à bord légèrement plus
// clair, rendus en BASSE RÉSOLUTION (quart de l'écran) puis agrandis en
// LinearFilter (R2 : le bokeh n'a aucun bord net, il n'y a rien à
// seuiller). Une seconde passe, plus petite encore et aux disques élargis,
// est la ville vue à travers la BUÉE : plus floue - et donc plus claire
// partout, par l'étalement de sa propre lumière, jamais par un gris ajouté
// (R3). Les disques ne sont pas un tableau d'uniformes : une texture de
// données 120 x 2 écrite une fois (position, rayon, couleur, rôle) ; la
// dérive, le clignotement et les voitures sont calculés dans le shader.
//
// LA VITRE, un seul quad plein écran (méthode du shader « Heartfelt » de
// BigWings, réécrite) :
//   - petites gouttes statiques : deux grilles décalées, une goutte au plus
//     par cellule, centre jittéré, apparition/disparition lente ;
//   - 20 grosses gouttes (10 au tier 0) posées sur la vitre ; une goutte
//     qui se détache descend PAR À-COUPS (y = y0 - v (T - k sin T), k < 1 :
//     monotone, quasi arrêts) le long d'un chemin qui serpente, et laisse
//     une TRAÎNÉE NETTE : la buée y est effacée, puis revient lentement ;
//   - chaque goutte est une LENTILLE : elle montre la ville INVERSÉE et
//     rétrécie, lue autour de son centre (uv = c - (p - c) * m).
// Les bords des gouttes sont antialiasés à la taille du pixel (R1).

// static drops: one grid at tiers 0-1, two at tier 2 (the glass pass is
// the Pi's cost: ~17 ms at tier 1 with two grids and a B-spline mist)
const TIER = {
  0: { disks: 60, drops: 10, bokehScale: 0.25, fogScale: 0.125 },
  1: { disks: 120, drops: 20, bokehScale: 0.25, fogScale: 0.125 },
  2: { disks: 120, drops: 20, bokehScale: 0.33, fogScale: 0.16 }
};
const MAX_DROPS = 20;
const FOG = 0.82;                    // how much of the glass is misted
const TRAIL_RECONDENSE_S = 14;       // a trail mists over again in ~14 s
const AMBIENT_RELEASE_S = [9, 22];   // a drop lets go now and then, off the beat
const NO_BPM_PHRASE_S = 30;
const ENERGY_BARS = 4;               // brightness follows energy over 4 bars
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SAFE_FLOOR = 0.7;              // the city dims a little behind the cover
const FALLBACK_PALETTE = [0xffb347, 0x3fc5ff, 0xff4f7b];
const LENS_MAG = 7.0;                // a drop shows ~7x its own size of city, inverted

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- the city: one instanced quad per disc, additive, at low resolution ---------------
// Not a loop over the discs in a full-screen pass: on the Pi, 120 discs x
// two texture reads per low-res pixel cost ~40 ms a frame. As geometry each
// fragment computes only the disc it belongs to. The disc table is two
// static instanced attributes; drift, blinking and the cars are computed in
// the vertex shader, nothing is uploaded per frame.
const bokehVertex = `
  precision highp float;
  attribute vec2 aCorner;        // -1..1
  attribute vec4 aDiskA;         // x, y (uv), radius (height units), role
  attribute vec4 aDiskC;         // r, g, b, phase
  uniform float uTime;
  uniform float uAspect;
  uniform mediump float uBlur;   // shared with the fragment shader: same precision
  varying vec2 vLocal;           // in units of the disc's radius
  varying vec3 vCol;
  varying float vR;
  void main() {
    vec2 pos = aDiskA.xy;
    float r = aDiskA.z;
    float role = aDiskA.w;            // 0 lamp, 1 blinking, 2 car
    float ph = aDiskC.a * 6.2832;
    float k = 1.0;
    if (role > 1.5) {
      // a car's light crossing the bottom of the frame, left to right
      pos.x = fract(pos.x + uTime * (0.012 + 0.010 * aDiskC.a)) * 1.2 - 0.1;
      k = smoothstep(-0.1, 0.05, pos.x) * smoothstep(1.1, 0.95, pos.x);
    } else {
      pos += vec2(sin(uTime * 0.021 + ph), cos(uTime * 0.017 + ph * 1.3)) * 0.012;
      if (role > 0.5) k = 0.35 + 0.65 * smoothstep(-0.2, 0.6, sin(uTime * (0.35 + 0.3 * aDiskC.a) + ph));
    }
    float rr = r * (1.0 + 0.9 * uBlur);           // through the mist: wider
    // far, out-of-focus lights: the bigger the disc, the less light per pixel
    float lum = 0.34 / (1.0 + r * 9.0);
    vCol = aDiskC.rgb * lum * k / (1.0 + uBlur * 1.6);
    vR = rr;
    vLocal = aCorner * 1.05;
    vec2 off = aCorner * rr * 1.05;
    gl_Position = vec4(pos.x * 2.0 - 1.0 + off.x / uAspect * 2.0, pos.y * 2.0 - 1.0 + off.y * 2.0, 0.0, 1.0);
  }
`;
const bokehFragment = `
  precision mediump float;
  uniform float uBright;
  uniform float uBlur;
  varying vec2 vLocal;
  varying vec3 vCol;
  varying float vR;
  void main() {
    float d = length(vLocal);                     // 1 at the disc's edge
    float soft = mix(0.10, 0.55, uBlur);
    float disc = smoothstep(1.0, 1.0 - soft, d);
    // a real lens: the rim a little brighter than the middle
    float rim = smoothstep(1.0 - soft * 2.2, 1.0 - soft * 0.6, d) * disc * (1.0 - uBlur) * 0.40;
    gl_FragColor = vec4(vCol * (disc + rim * 0.8) * uBright, 1.0);
  }
`;

// --- the drop mask: running drops, their trails, the big sitting drops ----------------
// One instanced quad per head and per trail, drawn into a full-resolution
// half-float mask with MAX blending. The glass then reads ONE texel instead
// of looping over twenty drops on every pixel (measured on the Pi: the loop
// alone was ~160 ms a frame at tier 1). Encoding:
//   R  how much of the mist is wiped (trail, or 1 inside a drop)
//   G  radius of the drop covering this pixel (0 = none)
//   BA offset from that drop's centre + 1 (height units, kept positive for MAX)
const PATH_GLSL = `
  // the path a running drop follows: it wanders a little as it goes down
  float pathX(float x0, float y, float seed) {
    return x0 + (sin(y * 17.0 + seed * 6.0) * 0.7 + sin(y * 41.0 + seed * 13.0) * 0.12) * 0.006;
  }
  float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
`;
const maskVertex = `
  precision highp float;
  attribute vec2 aCorner;        // -1..1
  attribute float aSlot;
  attribute float aKind;         // 0 trail, 1 head
  uniform vec4 uDropA[${MAX_DROPS}];   // x (uv), head y, start y, radius
  uniform vec4 uDropB[${MAX_DROPS}];   // trail clear, seed, running, presence
  uniform float uAspect;
  varying vec2 vP;
  varying vec4 vA;
  varying vec4 vB;
  varying float vKind;
  void main() {
    vec4 a = uDropA[int(aSlot)];
    vec4 b = uDropB[int(aSlot)];
    vA = a; vB = b; vKind = aKind;
    float x = a.x * uAspect;
    vec2 lo, hi;
    if (aKind < 0.5) {
      // the trail: from the head up to where the drop let go, path wiggle included
      float w = a.w * 0.85 + 0.008;
      lo = vec2(x - w, a.y - a.w);
      hi = vec2(x + w, a.z + a.w);
      if (b.x < 0.005) hi = lo;     // nothing to draw
    } else {
      float r = a.w * b.w * 1.3 + 0.004;
      lo = vec2(x - r - 0.008, a.y - r);
      hi = vec2(x + r + 0.008, a.y + r);
      if (b.w < 0.005) hi = lo;
    }
    vec2 p = mix(lo, hi, aCorner * 0.5 + 0.5);
    vP = p;
    gl_Position = vec4(p.x / uAspect * 2.0 - 1.0, p.y * 2.0 - 1.0, 0.0, 1.0);
  }
`;
const maskFragment = `
  precision highp float;
  uniform float uAspect;
  uniform vec2 uRes;
  varying vec2 vP;
  varying vec4 vA;
  varying vec4 vB;
  varying float vKind;
  ${PATH_GLSL}
  void main() {
    vec2 p = vP;
    float px = 1.0 / uRes.y;
    vec4 a = vA, b = vB;
    vec4 o = vec4(0.0);
    if (vKind < 0.5) {
      // the wiped streak
      float onTrail = step(a.y, p.y) * step(p.y, a.z);
      float tx = pathX(a.x, p.y, b.y) * uAspect;
      float w = a.w * 0.85;
      o.r = (1.0 - smoothstep(w - px, w + px, abs(p.x - tx))) * onTrail * b.x;
      // the little drops a trail leaves behind
      float cell = floor(p.y / (a.w * 2.2));
      float cy = (cell + 0.5) * a.w * 2.2;
      float keep = step(0.55, hash21(vec2(cell, b.y * 17.0)));
      vec2 td = p - vec2(pathX(a.x, cy, b.y) * uAspect, cy);
      float tr = a.w * 0.32 * keep * b.x;
      if (tr > 0.0 && onTrail > 0.5 && length(td) < tr + 2.0 * px) { o.g = tr; o.ba = td + 1.0; }
    } else {
      vec2 d = p - vec2(pathX(a.x, a.y, b.y) * uAspect, a.y);
      d.y *= mix(1.0, 0.8, b.z);           // a running drop is a little taller
      float r = a.w * b.w;
      float sd = length(d) - r;
      if (sd < 2.0 * px) { o.r = 1.0 - smoothstep(-px, px, sd); o.g = r; o.ba = d + 1.0; }
    }
    gl_FragColor = o;
  }
`;

// --- the glass: the city, the mist, the static drops, the mask --------------------------
function glassFragment(grids) {
  return `
  precision mediump float;
  uniform sampler2D uCity;       // through clear glass
  uniform sampler2D uMist;       // through the mist
  uniform highp sampler2D uMask; // running drops and trails (see maskFragment)
  uniform vec2 uCitySize;        // texel counts of the two low-res passes
  uniform vec2 uMistSize;
  uniform vec2 uRes;
  uniform float uAspect;
  uniform float uTime;
  uniform float uFog;
  uniform float uNoGlass;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  // Cubic B-spline upscale in four bilinear taps (R2: no visible pixel
  // block). Bilinear alone left a staircase on every disc edge of the
  // quarter-resolution city.
  vec3 texB(sampler2D t, vec2 uv, vec2 size) {
    vec2 st = uv * size - 0.5;
    vec2 i = floor(st);
    vec2 f = st - i;
    vec2 f2 = f * f, f3 = f2 * f;
    vec2 w0 = (-f3 + 3.0 * f2 - 3.0 * f + 1.0) / 6.0;
    vec2 w1 = (3.0 * f3 - 6.0 * f2 + 4.0) / 6.0;
    vec2 w2 = (-3.0 * f3 + 3.0 * f2 + 3.0 * f + 1.0) / 6.0;
    vec2 w3 = f3 / 6.0;
    vec2 g0 = w0 + w1, g1 = w2 + w3;
    vec2 h0 = (i - 0.5 + w1 / g0) / size;
    vec2 h1 = (i + 1.5 + w3 / g1) / size;
    vec3 a = texture2D(t, vec2(h0.x, h0.y)).rgb, b = texture2D(t, vec2(h1.x, h0.y)).rgb;
    vec3 c = texture2D(t, vec2(h0.x, h1.y)).rgb, d = texture2D(t, vec2(h1.x, h1.y)).rgb;
    return g0.y * (g0.x * a + g1.x * b) + g1.y * (g0.x * c + g1.x * d);
  }
  float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  vec2 hash22(vec2 p) { float n = hash21(p); return vec2(n, hash21(p + n)); }
  float safeAtt(vec2 uv) {
    vec2 s = vec2((uv.x * 2.0 - 1.0) * uAspect, uv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = vec2(uv.x * uAspect, uv.y);            // height units
    float px = 1.0 / uRes.y;                        // one pixel, in height units
    vec3 city = texB(uCity, uv, uCitySize);
    // the mist is blurred far beyond its texel: bilinear is enough (one read, not four)
    vec3 mist = texture2D(uMist, uv).rgb;
    if (uNoGlass > 0.5) { gl_FragColor = vec4(city * safeAtt(uv), 1.0); return; }

    // --- running drops, trails, big drops: one texel of the mask
    vec4 m = texture2D(uMask, uv);
    float clear = clamp(m.r, 0.0, 1.0);
    float lens = 0.0;
    vec2 lensC = p;
    float lensR = 1.0;
    if (m.g > 0.0) {
      vec2 off = m.ba - 1.0;
      lensR = m.g;
      lensC = p - off;
      lens = 1.0 - smoothstep(-px, px, length(off) - lensR);
    }

    // --- small static drops: two offset grids, one drop at most per cell,
    // slowly forming and evaporating; wiped where a trail passed
    for (int g = 0; g < ${grids}; g++) {
      float sz = g == 0 ? 0.028 : 0.017;
      vec2 sh = g == 0 ? vec2(0.0) : vec2(0.5, 0.37);
      vec2 gp = p / sz + sh;
      // each row slides by its own amount: no column of drops lines up
      gp.x += hash21(vec2(floor(gp.y), float(g) + 7.0)) * 3.0;
      vec2 id = floor(gp);
      vec2 f = fract(gp);
      vec2 j = hash22(id + float(g) * 31.7);
      vec2 c = 0.22 + 0.56 * j;
      float life = fract(uTime * 0.012 * (0.5 + j.x) + j.y);
      float present = step(0.42, hash21(id * 1.7 + float(g))) * smoothstep(0.0, 0.15, life) * smoothstep(1.0, 0.8, life);
      float r = (0.09 + 0.12 * hash21(id + 3.1)) * present;   // <= 0.21: stays inside its cell
      float sd = (length(f - c) - r) * sz;
      float inside = (1.0 - smoothstep(-px, px, sd)) * step(0.001, r) * (1.0 - clear);
      if (inside > lens) { lens = inside; lensC = (id + c - sh - vec2(hash21(vec2(id.y, float(g) + 7.0)) * 3.0, 0.0)) * sz; lensR = r * sz; }
    }

    // --- the mist: blurrier and lighter everywhere, except where it was wiped
    float fog = uFog * (1.0 - clear);
    vec3 col = mix(city, mist * 1.35, fog);
    // --- the lens: the city INVERTED and shrunk, seen sharp through the water
    vec2 q = lensC - (p - lensC) * ${LENS_MAG.toFixed(1)};
    vec2 quv = vec2(q.x / uAspect, q.y);
    vec3 inv = texture2D(uCity, clamp(quv, 0.0, 1.0)).rgb * 1.25;
    // water gathers the glow around it too: without it a drop over a dark
    // gap of the city read as a black speck, dirt on the glass
    inv += mist * 0.45;
    // the drop's edge refracts less back: a thin darker ring, a small highlight
    float dl = length(p - lensC);
    float edge = smoothstep(lensR * 0.6, lensR, dl);
    vec3 drop = inv * (1.0 - 0.30 * edge);
    float hl = 1.0 - smoothstep(0.0, lensR * 0.28, length(p - lensC - vec2(-0.3, 0.35) * lensR));
    drop += vec3(0.9, 0.92, 1.0) * hl * 0.35;
    col = mix(col, drop, lens);
    gl_FragColor = vec4(col * safeAtt(uv), 1.0);
  }
`;
}

const MATERIALS = new Map();

export class RainwindowVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    this._noGlass = urlParam('rainnoglass') === '1';
    const f = parseFloat(urlParam('rainfog'));
    this._fog = Number.isFinite(f) ? clamp01(f) : FOG;
    // lounge (R9): soft bloom, no aberration pulse, no flash
    this._post = { bloomStrength: 0.35, bloomThreshold: 0.80, vignette: 0.40, grain: 0.02, aberration: 0.0008, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._post; }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  init(scene, camera) {
    this._scene = scene;
    this._seed = 4242;
    const L = this._layout;
    this._nDrops = L.drops;

    // the disc table, written once (the colours again when the cover changes)
    const n = L.disks;
    this._nDisks = n;
    this._diskData = new Float32Array(n * 2 * 4);
    this._diskRole = [];
    for (let i = 0; i < n; i++) {
      const car = i < Math.round(n * 0.12);
      const blink = !car && this._random() < 0.18;
      const r = car ? 0.014 + this._random() * 0.012 : 0.02 + Math.pow(this._random(), 2.2) * 0.11;
      // the city sits low: more lamps in the lower two thirds, cars at the bottom
      const y = car ? 0.06 + this._random() * 0.12 : 0.12 + Math.pow(this._random(), 0.8) * 0.8;
      this._diskData[i * 4] = this._random();
      this._diskData[i * 4 + 1] = y;
      this._diskData[i * 4 + 2] = r;
      this._diskData[i * 4 + 3] = car ? 2 : blink ? 1 : 0;
      this._diskRole.push(car ? 'car' : blink ? 'blink' : 'lamp');
      this._diskData[(n + i) * 4 + 3] = this._random();
    }
    this._paletteSeen = undefined;
    this._applyPalette({ artworkPalette: null });

    // the two low-resolution passes
    this._cityBuf = new FeedbackBuffer({ scale: L.bokehScale, single: true });
    this._mistBuf = new FeedbackBuffer({ scale: L.fogScale, single: true });
    {
      const geo = new THREE.InstancedBufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
      geo.setAttribute('aCorner', new THREE.BufferAttribute(new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), 2));
      geo.setIndex([0, 1, 2, 2, 1, 3]);
      this._diskA = new THREE.InstancedBufferAttribute(this._diskData.subarray(0, n * 4), 4);
      this._diskC = new THREE.InstancedBufferAttribute(this._diskData.subarray(n * 4, n * 8), 4);
      geo.setAttribute('aDiskA', this._diskA);
      geo.setAttribute('aDiskC', this._diskC);
      geo.instanceCount = n;
      this._bU = { uTime: { value: 0 }, uAspect: { value: 1.78 }, uBright: { value: 1 }, uBlur: { value: 0 } };
      const bm = new THREE.ShaderMaterial({
        uniforms: this._bU, vertexShader: bokehVertex, fragmentShader: bokehFragment,
        transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
      });
      this._passScene = new THREE.Scene();
      this._passQuad = new THREE.Mesh(geo, bm);
      this._passQuad.frustumCulled = false;
      this._passScene.add(this._passQuad);
    }
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    // the glass
    const grids = this._tier >= 2 ? 2 : 1;
    const gk = 'glass:' + grids;
    let gm = MATERIALS.get(gk);
    if (!gm) {
      gm = new THREE.ShaderMaterial({
        uniforms: {
          uCity: { value: null }, uMist: { value: null }, uMask: { value: null }, uCitySize: { value: new THREE.Vector2(1, 1) }, uMistSize: { value: new THREE.Vector2(1, 1) }, uRes: { value: new THREE.Vector2(1280, 720) }, uAspect: { value: 1.78 },
          uTime: { value: 0 }, uFog: { value: FOG }, uNoGlass: { value: 0 },
          uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
        },
        vertexShader: quadVertex, fragmentShader: glassFragment(grids), depthTest: false, depthWrite: false
      });
      MATERIALS.set(gk, gm);
    }
    // the drop mask: two instanced quads per slot (trail, head), MAX blended
    {
      const geo = new THREE.InstancedBufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
      geo.setAttribute('aCorner', new THREE.BufferAttribute(new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), 2));
      geo.setIndex([0, 1, 2, 2, 1, 3]);
      const slot = new Float32Array(this._nDrops * 2), kind = new Float32Array(this._nDrops * 2);
      for (let i = 0; i < this._nDrops; i++) { slot[i * 2] = i; kind[i * 2] = 0; slot[i * 2 + 1] = i; kind[i * 2 + 1] = 1; }
      geo.setAttribute('aSlot', new THREE.InstancedBufferAttribute(slot, 1));
      geo.setAttribute('aKind', new THREE.InstancedBufferAttribute(kind, 1));
      geo.instanceCount = this._nDrops * 2;
      const A = [], B = [];
      for (let i = 0; i < MAX_DROPS; i++) { A.push(new THREE.Vector4(0, -1, -1, 0)); B.push(new THREE.Vector4(0, 0, 0, 0)); }
      this._mU = { uDropA: { value: A }, uDropB: { value: B }, uAspect: { value: 1.78 }, uRes: { value: new THREE.Vector2(1280, 720) } };
      const mm = new THREE.ShaderMaterial({
        uniforms: this._mU, vertexShader: maskVertex, fragmentShader: maskFragment,
        depthTest: false, depthWrite: false, transparent: true,
        blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
      });
      this._maskScene = new THREE.Scene();
      this._maskMesh = new THREE.Mesh(geo, mm);
      this._maskMesh.frustumCulled = false;
      this._maskScene.add(this._maskMesh);
      this._maskBuf = new FeedbackBuffer({ scale: 1, single: true, type: THREE.HalfFloatType, filter: THREE.NearestFilter });
    }
    this._glassMat = gm; this._gU = gm.uniforms;
    this._gU.uFog.value = this._fog;
    this._gU.uNoGlass.value = this._noGlass ? 1 : 0;
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), gm);
    this._quad.frustumCulled = false; this._quad.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // the drops
    this._drops = [];
    for (let i = 0; i < this._nDrops; i++) this._drops.push(this._newDrop(true));
    this._time = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phrasePhase = null;
    this._energy = 0.5;
    this._nextAmbient = this._time + this._ambientGap();
    this._noBpmPhrase = 0;
    this._marksRef = undefined; this._dropMarks = []; this._dropIdx = 0; this._lastNowMs = null;
    this._writeDrops();
  }

  _ambientGap() { return AMBIENT_RELEASE_S[0] + this._random() * (AMBIENT_RELEASE_S[1] - AMBIENT_RELEASE_S[0]); }

  // a big drop sitting on the glass, somewhere in the upper part
  _newDrop(initial) {
    return {
      x: 0.04 + this._random() * 0.92,
      y0: 0.35 + this._random() * 0.6,
      r: 0.016 + this._random() * 0.012,
      seed: this._random(),
      state: 'sit',                       // sit -> run -> gone (trail fades) -> sit
      t: 0, head: 0,
      presence: initial ? 1 : 0,
      clear: 0, v: 0.028 + this._random() * 0.022, w: 1.6 + this._random() * 1.2
    };
  }

  // release the sitting drop that has sat the longest
  _release(count) {
    const sitting = this._drops.filter((d) => d.state === 'sit' && d.presence > 0.9);
    sitting.sort(() => this._random() - 0.5);
    for (let k = 0; k < Math.min(count, sitting.length); k++) {
      const d = sitting[k];
      d.state = 'run'; d.t = 0; d.head = d.y0; d.clear = 1;
    }
  }

  _applyPalette(profile) {
    if (profile.artworkPalette === this._paletteSeen) return;
    this._paletteSeen = profile.artworkPalette;
    const v = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length
      ? profile.artworkPalette.vividColors : FALLBACK_PALETTE;
    const cols = v.slice(0, 3).map((c) => vdjColor(c, null, 0.85));
    const warm = [new THREE.Color(1.0, 0.78, 0.52), new THREE.Color(1.0, 0.86, 0.66), new THREE.Color(1.0, 0.70, 0.42)];
    const n = this._nDisks;
    let s = 77;
    const rnd = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
    for (let i = 0; i < n; i++) {
      let c;
      if (this._diskRole[i] === 'car') c = rnd() < 0.5 ? new THREE.Color(1.0, 0.18, 0.12) : new THREE.Color(1.0, 0.92, 0.8);
      else c = rnd() < 0.55 ? warm[Math.floor(rnd() * 3)] : cols[Math.floor(rnd() * cols.length)];
      // a bigger disc is dimmer per pixel: far, out-of-focus lights
      const k = 0.5 + 0.5 * rnd();
      this._diskData[(n + i) * 4] = c.r * k;
      this._diskData[(n + i) * 4 + 1] = c.g * k;
      this._diskData[(n + i) * 4 + 2] = c.b * k;
    }
    // the colours change with the cover only: one upload then, none per frame
    if (this._diskC) this._diskC.needsUpdate = true;
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._dropMarks = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._dropMarks.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._dropMarks.length && this._dropMarks[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._dropMarks.length && this._dropMarks[this._dropIdx] <= now) {
      if (now - this._dropMarks[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    // pause: everything holds where it is - the frame stays, never black
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._applyPalette(profile);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const grid = profile.beatPhase != null && bpm > 0;
    const barS = 4 * (grid ? 60 / bpm : 60 / 80);

    // PHRASE: one drop lets go. No grid: every NO_BPM_PHRASE_S.
    let phrase = false;
    const ph = profile.phrasePhase;
    if (grid && ph != null) { if (this._phrasePhase != null && ph < this._phrasePhase - 0.5) phrase = true; this._phrasePhase = ph; }
    else { this._noBpmPhrase += dt; if (this._noBpmPhrase >= NO_BPM_PHRASE_S) { this._noBpmPhrase = 0; phrase = true; } }
    if (phrase) this._release(1);
    // DROP: five together
    if (grid && this._tickDrops(profile)) this._release(5);
    // and now and then one on its own, at a random moment: the glass lives
    // without ever showing the tempo
    if (this._time >= this._nextAmbient) { this._release(1); this._nextAmbient = this._time + this._ambientGap(); }

    // energy, smoothed over 4 bars: the city breathes
    let e = director && director.intensity != null ? director.intensity
      : (profile.trackFeatures && profile.trackFeatures.energy != null ? profile.trackFeatures.energy / 100 : 0.4);
    e = clamp01(e);
    this._energy += (e - this._energy) * (1 - Math.exp(-dt / (ENERGY_BARS * barS)));

    // the drops
    for (const d of this._drops) {
      if (d.state === 'sit') { d.presence = Math.min(1, d.presence + dt / 3); continue; }
      d.t += dt;
      if (d.state === 'run') {
        // stop-and-go: T - k sin(T) is monotonic for k < 1 and nearly stops
        const T = d.t * d.w;
        const s = (T - 0.92 * Math.sin(T)) / d.w;
        d.head = d.y0 - d.v * s * (1 + d.t * 0.04);
        d.clear = 1;
        if (d.head < -0.05) { d.state = 'gone'; d.t = 0; }
      } else if (d.state === 'gone') {
        // the mist comes back over the trail, then a new drop forms elsewhere
        d.clear = Math.exp(-d.t / (TRAIL_RECONDENSE_S / 3));
        if (d.t > TRAIL_RECONDENSE_S) Object.assign(d, this._newDrop(false));
      }
    }
    this._writeDrops();

    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    this._bU.uTime.value = this._time;
    this._bU.uAspect.value = aspect;
    this._bU.uBright.value = 0.55 + 0.6 * this._energy;
    this._gU.uTime.value = this._time;
    this._gU.uAspect.value = aspect;
    this._mU.uAspect.value = aspect;
  }

  _writeDrops() {
    const A = this._mU.uDropA.value, B = this._mU.uDropB.value;
    for (let i = 0; i < MAX_DROPS; i++) {
      const d = this._drops[i];
      if (!d) { B[i].set(0, 0, 0, 0); continue; }
      const running = d.state === 'run';
      const head = d.state === 'sit' ? d.y0 : (running ? d.head : -1);
      // gone: the head is off the glass but the trail (y0 .. bottom) still shows
      A[i].set(d.x, d.state === 'gone' ? -0.1 : head, d.state === 'sit' ? d.y0 : d.y0, d.r);
      B[i].set(d.state === 'sit' ? 0 : d.clear, d.seed, running ? 1 : 0, d.state === 'gone' ? 0.001 : d.presence);
    }
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    this._cityBuf.ensureSize(renderer);
    this._mistBuf.ensureSize(renderer);
    const cc0 = new THREE.Color(); renderer.getClearColor(cc0); const ca0 = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    this._bU.uBlur.value = 0;
    renderer.setRenderTarget(this._cityBuf.read);
    renderer.clear(true, false, false);
    renderer.render(this._passScene, this._ortho);
    this._bU.uBlur.value = 1;
    renderer.setRenderTarget(this._mistBuf.read);
    renderer.clear(true, false, false);
    renderer.render(this._passScene, this._ortho);
    renderer.setClearColor(cc0, ca0);
    renderer.getDrawingBufferSize(this._size);
    this._gU.uRes.value.copy(this._size);
    this._mU.uRes.value.copy(this._size);
    this._maskBuf.ensureSize(renderer);
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._maskBuf.read);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, false, false);
    renderer.render(this._maskScene, this._ortho);
    renderer.setClearColor(cc, ca);
    renderer.setRenderTarget(prev);
    this._gU.uMask.value = this._maskBuf.read.texture;
    this._gU.uCity.value = this._cityBuf.read.texture;
    this._gU.uCitySize.value.set(this._cityBuf.read.width, this._cityBuf.read.height);
    this._gU.uMistSize.value.set(this._mistBuf.read.width, this._mistBuf.read.height);
    this._gU.uMist.value = this._mistBuf.read.texture;
  }

  dispose() {
    if (this._scene && this._quad) { this._quad.onBeforeRender = null; this._scene.remove(this._quad); this._quad.geometry.dispose(); }
    if (this._passQuad) { this._passQuad.geometry.dispose(); this._passQuad.material.dispose(); }
    if (this._cityBuf) this._cityBuf.dispose();
    if (this._mistBuf) this._mistBuf.dispose();
    if (this._maskBuf) this._maskBuf.dispose();
    if (this._maskMesh) { this._maskMesh.geometry.dispose(); this._maskMesh.material.dispose(); }
    this._scene = null;
  }
}
